import { useState, useEffect, memo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import CONFIG from '../../../config';
import { getToken } from '../../../utils/auth';

const ConfigPanel = memo(() => {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${CONFIG.API_URL}/api/admin/configs/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(res.data);
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch config stats:', error);
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
        );
    }

    if (!stats) return null;

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <span>⚙️</span>
                    {t("admin.analytics_page.config_panel.title", "App Settings")}
                </h3>
                <span className={`text-xs px-3 py-1 rounded-full font-medium ${stats.status === 'healthy' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                    {stats.status.toUpperCase()}
                </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="text-3xl font-black text-gray-900">{stats.total}</div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider font-bold">{t("admin.analytics_page.config_panel.total_configs", "Total Configs")}</div>
                </div>
                <div className="p-4 bg-brand-primary/5 rounded-xl">
                    <div className="text-3xl font-black text-brand-primary">{stats.active}</div>
                    <div className="text-xs text-brand-primary/60 uppercase tracking-wider font-bold">{t("admin.analytics_page.config_panel.active", "Active")}</div>
                </div>
            </div>

            <div className="space-y-3">
                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">{t("admin.analytics_page.config_panel.recently_updated", "Recently Updated")}</h4>
                {stats.recent.map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-sm p-2 hover:bg-gray-50 rounded-lg transition-colors">
                        <span className="font-mono text-gray-700">{c.config_key}</span>
                        <span className="text-gray-400 text-xs">{new Date(c.updated_at).toLocaleDateString()}</span>
                    </div>
                ))}
                {stats.recent.length === 0 && <p className="text-gray-400 text-xs italic">{t("admin.analytics_page.config_panel.no_configs", "No configurations found.")}</p>}
            </div>
        </div>
    );
});

export default ConfigPanel;
